import {
  Avatar,
  DataTable,
  DataTableColumn,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Eye, Mail, Phone } from 'lucide-react';
import { useMemo } from 'react';
import type { Advisory } from '../../../services';
import { useDataTablePageSync } from '../../../shared/hooks';
import { SearchFilterField, SelectFilterField } from '../../../shared/ui';
import { Page, PageHeader } from '../../../shared/ui/page';
import { f, useFilters } from '../../../shared/utils';
import { formatPhone } from '../../../shared/utils/format-phone';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import { useAdvisories } from '../hooks';

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Ativo', value: 'active' },
  { label: 'Inativo', value: 'inactive' },
];

function AdvisoryStatusTag({ status }: { status: string }) {
  const isActive = status === 'active';
  return (
    <Tag.Root
      variant='outline'
      colorScheme={isActive ? 'success' : 'gray'}
      size='sm'
    >
      <Tag.Label>{isActive ? 'Ativo' : 'Inativo'}</Tag.Label>
    </Tag.Root>
  );
}

const DEFAULT_SORT_FIELD = 'PartyName';
const DEFAULT_SORT_TYPE = 'asc';

const ADVISORY_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  name: 'PartyName',
  cnpj: 'OrganizationIdNumber',
  responsibleName: 'responsibleName',
};

const ADVISORY_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  PartyName: 'name',
  OrganizationIdNumber: 'cnpj',
  responsibleName: 'responsibleName',
};

export function AdvisoriesPage() {
  const navigate = useNavigate();

  const columns = useMemo<DataTableColumn<Advisory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        size: 400,
        cell: ({ row }) => {
          const advisory = row.original;
          return (
            <div className='flex items-center gap-3'>
              <Avatar.Root
                size='md'
                shape='circle'
                className='bg-alba-blue-500'
              >
                <Building2 className='w-4 h-4 text-white' />
              </Avatar.Root>
              <div className='flex flex-col gap-1'>
                <span className='font-gotham font-bold text-alba-black-500'>
                  {advisory.name}
                </span>
                <div className='flex items-center gap-1'>
                  <Mail className='w-4 h-4 text-alba-gray-600' />
                  <span className='text-alba-gray-600 text-xs'>
                    {advisory.contactEmail || 'Email não disponível'}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'cnpj',
        header: 'CNPJ',
        size: 200,
        cell: ({ row }) => {
          const advisory = row.original;
          return (
            <span className='text-alba-gray-700 text-sm'>
              {formatRegisterNumber(advisory.cnpj)}
            </span>
          );
        },
      },
      {
        accessorKey: 'responsibleName',
        header: 'Responsável',
        size: 300,
        cell: ({ row }) => {
          const advisory = row.original;
          return (
            <div className='flex flex-col gap-1'>
              <span className='text-alba-black-500 text-sm font-medium'>
                {advisory.responsibleName || '-'}
              </span>
              <div className='flex items-center gap-1'>
                <Phone className='w-4 h-4 text-alba-gray-600' />
                <span className='text-alba-gray-600 text-xs'>
                  {formatPhone(advisory.responsiblePhone)}
                </span>
              </div>
              {advisory.responsibleEmail && (
                <div className='flex items-center gap-1'>
                  <Mail className='w-4 h-4 text-alba-gray-600' />
                  <span className='text-alba-gray-600 text-xs'>
                    {advisory.responsibleEmail}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        sortable: false,
        cell: ({ row }) => {
          const advisory = row.original;
          const isActive = advisory.status === 'active';
          return (
            <Tag.Root
              variant='outline'
              colorScheme={isActive ? 'success' : 'gray'}
              size='sm'
            >
              <Tag.Label>{isActive ? 'Ativo' : 'Inativo'}</Tag.Label>
            </Tag.Root>
          );
        },
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 50,
        sortable: false,
        cell: ({ row }) => {
          const advisory = row.original;
          return (
            <div className='flex justify-end'>
              <Menu.Root>
                <Menu.Trigger />
                <Menu.Content>
                  <Menu.Item
                    value='view'
                    icon={<Eye size={16} />}
                    onSelect={() =>
                      navigate({
                        to: '/advisories/$id',
                        params: { id: advisory.partyCode },
                      })
                    }
                  >
                    Ver detalhes
                  </Menu.Item>
                </Menu.Content>
              </Menu.Root>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(5),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<Advisory>(filters.pageNo);

  const { data, isLoading, error } = useAdvisories(debouncedFilters);
  const advisories = data?.advisories || [];
  const totalPages = data?.pagination.totalPages || 0;
  const totalItems = data?.pagination.total || 0;
  const sorting = useMemo(() => {
    const columnId = ADVISORY_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'desc',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          { label: 'Página Inicial', href: '/' },
          { label: 'Assessorias', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Assessorias</PageHeader.Title>
            <PageHeader.Subtitle>
              Gerencie as assessorias parceiras
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
        </PageHeader.Main>
      </PageHeader.Root>

      <PageHeader.Filters>
        <div className='flex flex-col gap-4 md:flex-row lg:max-w-xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='md:min-w-[180px]'
            onValueChange={() => setFilters({ ...filters, pageNo: 1 })}
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar assessorias. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-4 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map((_, index) => (
                  <div
                    key={index}
                    className='h-56 animate-pulse rounded-3xl bg-alba-gray-200'
                  />
                ))
              ) : (
                advisories.map(advisory => (
                  <article
                    key={advisory.partyCode}
                    className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex min-w-0 items-center gap-3'>
                        <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
                          <Building2 className='w-4 h-4 text-white' />
                        </Avatar.Root>
                        <div className='min-w-0'>
                          <p className='truncate font-gotham font-bold text-alba-black-500'>
                            {advisory.name}
                          </p>
                          <p
                            className='mt-1 truncate text-xs text-alba-gray-600'
                            title={advisory.contactEmail || 'Email não disponível'}
                          >
                            {advisory.contactEmail || 'Email não disponível'}
                          </p>
                        </div>
                      </div>
                      <Menu.Root>
                        <Menu.Trigger />
                        <Menu.Content>
                          <Menu.Item
                            value='view'
                            icon={<Eye size={16} />}
                            onSelect={() =>
                              navigate({
                                to: '/advisories/$id',
                                params: { id: advisory.partyCode },
                              })
                            }
                          >
                            Ver detalhes
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Root>
                    </div>

                    <div className='mt-4 space-y-3'>
                      <div>
                        <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                          CNPJ
                        </p>
                        <p className='text-sm text-alba-gray-700'>
                          {formatRegisterNumber(advisory.cnpj)}
                        </p>
                      </div>
                      <div>
                        <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                          Responsável
                        </p>
                        <p className='text-sm font-medium text-alba-black-500'>
                          {advisory.responsibleName || '-'}
                        </p>
                        <p className='mt-1 text-xs text-alba-gray-600'>
                          {formatPhone(advisory.responsiblePhone)}
                        </p>
                        {advisory.responsibleEmail && (
                          <p className='mt-1 truncate text-xs text-alba-gray-600'>
                            {advisory.responsibleEmail}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                          Status
                        </p>
                        <AdvisoryStatusTag status={advisory.status} />
                      </div>
                    </div>
                  </article>
                ))
              )}

              {!isLoading && !advisories.length && (
                <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhuma assessoria encontrada.
                </div>
              )}

              {totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={totalPages * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={totalItems}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({ ...filters, pageNo: page.page, pageSize: page.pageSize })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                ref={dataTableRef}
                className='min-w-0'
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={totalPages}
                data={advisories}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? ADVISORY_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    pageNo: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'desc' : 'asc',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
    </Page.Root>
  );
}
